import type { Staff } from "@/lib/authv2/store";
import { ACTION } from "@/lib/auth/actions";
import { hasPermission } from "@/lib/authv2/permissions";
import { isShiftLeadTitle, SALE_DEPTS } from "@/lib/auth/page-access";
import type { ChatMe } from "./api";

/**
 * Phòng trực hộp thư — lễ tân, CSKH và hậu kỳ mang vai `staff` nên KHÔNG có
 * quyền nào; họ vào bằng phòng ban. Phản chiếu đúng
 * `auth.AllowOrDept("customer_chat.read", "reception", "cskh", "retouch")` ở
 * máy chủ, kể cả cách chuẩn hoá chuỗi — lệch nhau là menu hiện mà vào ăn 403,
 * hoặc ngược lại.
 */
const CHAT_DEPTS = new Set(["reception", "cskh", "retouch"]);

/**
 * Phòng chỉ ĐỌC. Hậu kỳ xem được khách của đơn mình chỉnh ảnh nhưng không trả
 * lời họ (CEO chốt 24/09/2026): chăm khách là việc của CSKH và lễ tân.
 *
 * Máy chủ mới là chốt chặn thật (`Service.requireWritable` trả 403 kèm lý do);
 * đây chỉ để khoá sẵn ô soạn tin, theo luật "nút mà vai không có quyền bấm thì
 * khoá sẵn và nói lý do" — đừng để bấm rồi mới ăn 403.
 */
const READ_ONLY_DEPTS = new Set(["retouch"]);

/**
 * Vai chuỗi (trưởng cửa hàng, quản lý cụm, QC) đi bằng QUYỀN, không bằng
 * phòng ban — phạm vi của họ là địa bàn được gán, không phải nghề của phòng
 * họ xuất thân.
 *
 * Loại trừ ở đây là bắt buộc: một QC phòng hậu kỳ mang `department = "retouch"`
 * nên nhánh phòng ban sẽ nhận nhầm họ là thợ hậu kỳ rồi khoá mất ô soạn tin,
 * trong khi máy chủ cho họ trả lời khách cả miền.
 */
const CHAIN_SCOPED_ROLES = new Set(["branch_manager", "area_manager", "qc"]);

/**
 * Vai không trả lời khách dù mang phòng trực hộp thư: chỉ xem, Điều phối (đã
 * khoá — QĐ 0050), Trưởng ca (đã khoá — QĐ 0228). Gương `auth.IsSaleChatUser` /
 * `grantCustomerChatReadToFrontDeskDepts` ở máy chủ.
 */
const READ_ONLY_ROLES = new Set(["viewer", "coordinator", "shift_lead"]);

const norm = (v: string | undefined | null) => (v || "").toLowerCase().trim();

function isChainScoped(staff: Staff): boolean {
  if (CHAIN_SCOPED_ROLES.has(norm(staff.role))) return true;
  // Trưởng ca (QĐ 0425) đi nhánh vai chuỗi như Trưởng cửa hàng — trừ người
  // phòng CSKH / kinh doanh, giữ việc chính. Gương `shiftLead` trong
  // `customerchat.ScopeFromUser`.
  return isShiftLeadTitle(staff) && norm(staff.department) !== "cskh" && !isSaleChatUser(staff);
}

function chatDeptOf(staff: Staff): string {
  if (isChainScoped(staff)) return "";
  const dept = norm(staff.department);
  return CHAT_DEPTS.has(dept) ? dept : "";
}

/**
 * Cổng vào mục "Khách hàng" của trang /customer-chats.
 *
 * Chỉ trả lời "có được vào trang không". Ai thấy khách nào là việc của máy
 * chủ (quyết định 0062, sửa 24/09/2026): quản trị / giám đốc / CSKH thấy tất
 * cả; lễ tân và trưởng cửa hàng thấy khách của cơ sở mình; quản lý cụm thấy cả
 * cụm; QC thấy cả miền chứa cơ sở được gán; hậu kỳ chỉ thấy khách của đơn mình
 * chỉnh ảnh; kinh doanh thấy khách mới chưa ai nhận + khách mình phụ trách (QĐ
 * 0275 — vào bằng `customer_chat.read` do máy chủ cộng). Giao diện không tự
 * đoán phạm vi — nó hiển thị đúng thứ máy chủ trả.
 */
export function canManageCustomerChats(staff: Staff | null): boolean {
  if (!staff) return false;
  // CustomerChatAdmin ("thấy tất cả") bao hàm quyền vào trang. Kiểm cả hai vì
  // hai quyền được cấp độc lập: một tài khoản chỉ có `.admin` mà thiếu `.read`
  // là chuyện có thật (Casdoor cấp theo vai, và hai vai không trùng nhau).
  return (
    hasPermission(ACTION.CustomerChatRead) ||
    hasPermission(ACTION.CustomerChatAdmin) ||
    chatDeptOf(staff) !== ""
  );
}

/**
 * Được trả lời khách hay không — quyết định ô soạn tin hiện hay khoá.
 *
 * Vào được trang KHÔNG đồng nghĩa với trả lời được: phòng hậu kỳ xem để nắm
 * việc, còn khách do CSKH và lễ tân chăm.
 */
export function canReplyCustomerChats(staff: Staff | null): boolean {
  if (!canManageCustomerChats(staff)) return false;
  if (!staff) return false;
  // Vai chỉ xem và hai vai đã khoá vào trang bằng cửa XEM theo phòng, nhưng cửa
  // ghi đóng với họ ở máy chủ (kiểm thử đóng vai 17/10/2026, Q01) — khoá sẵn ô
  // soạn tin, không tự đánh dấu đã đọc.
  if (READ_ONLY_ROLES.has(norm(staff.role))) return false;
  return !READ_ONLY_DEPTS.has(chatDeptOf(staff));
}

/**
 * Mở kênh realtime hay không — hỏi MÁY CHỦ (`GET /customer-chats/me`
 * → `realtime`), không tự đoán theo vai (QĐ 0223 §H).
 *
 * Từ đợt 1 mọi vai vào được trang đều nối được socket; máy chủ lọc từng sự kiện
 * theo cơ sở của người xem. Riêng phạm vi hậu kỳ (theo người chỉnh ảnh) thì
 * socket không bao giờ đẩy gì — `realtime: false`, giao diện tải lại theo chu
 * kỳ 20 giây thay vì giữ một kết nối câm. Chưa tải xong `me` thì CHƯA mở:
 * useChatSocket tự nối lại vô hạn, mở nhầm là gõ cửa máy chủ suốt ca làm.
 */
export function canUseCustomerChatSocket(me: ChatMe | null | undefined): boolean {
  return me?.realtime === true;
}

/**
 * Được sửa DANH MỤC thẻ phân loại (thêm / đổi tên, màu / xoá / sắp xếp) —
 * quyết định 0135. Gắn thẻ và ghim thì KHÔNG cần quyền này: ai vào được hộp
 * thư và trả lời được khách đều làm được trong phạm vi của mình.
 *
 * Phải kèm vế "trả lời được": trưởng phòng hậu kỳ mang vai `manager` nên cầm
 * sẵn `customer_chat.tag.manage`, mà từ 24/09/2026 họ vào được trang — thiếu
 * vế đó là phòng chỉ-xem đổi tên và xoá thẻ dùng chung của phòng chăm khách.
 * Máy chủ gác lại bằng middleware `requireWritableScope`; đây chỉ để ẩn nút.
 */
export function canManageChatTags(staff: Staff | null): boolean {
  return (
    hasPermission(ACTION.CustomerChatTagManage) &&
    canReplyCustomerChats(staff) &&
    !isSaleChatUser(staff)
  );
}

/**
 * Người kinh doanh trực hộp thư (QĐ 0275, nới ra cả hộp thư 04/10/2026): vai `sale`,
 * hoặc vai `staff` / `manager` của phòng kinh doanh. Phản chiếu
 * `auth.IsSaleChatUser` ở máy chủ.
 *
 * Họ vào trang bằng `customer_chat.read` — máy chủ tự cộng quyền đó cho kinh
 * doanh, nên `canManageCustomerChats` không cần nhánh riêng. Hàm này chỉ để
 * ẩn nút sửa DANH MỤC thẻ: trưởng phòng kinh doanh mang vai `manager` nên cầm
 * sẵn `customer_chat.tag.manage`, mà danh mục là của phòng chăm khách. Máy chủ
 * gác lại bằng `requireWritableScope` (403 kèm lý do).
 */
export function isSaleChatUser(staff: Staff | null): boolean {
  if (!staff) return false;
  const role = norm(staff.role);
  if (role === "sale") return true;
  if (role !== "staff" && role !== "manager") return false;
  return SALE_DEPTS.has(norm(staff.department));
}
